import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Observable, startWith } from 'rxjs';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  FrontendLanguage,
  DEFAULT_LANG,
  DEFAULT_TRANSLATION_LANG,
  LANGUAGE_STORE_KEY,
  SUPPORTED_LANGS,
} from '../../utils/languageUtils';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  constructor(private translateService: TranslateService) {}

  public setLanguage$(language: FrontendLanguage): Observable<unknown> {
    localStorage.setItem(LANGUAGE_STORE_KEY, language);
    document.documentElement.lang = language;
    return this.translateService.use(language);
  }

  public initLanguage$(): Observable<unknown> {
    let initialLanguage = localStorage.getItem(LANGUAGE_STORE_KEY) ?? undefined;
    if (!initialLanguage) {
      // if no initial language is found we use the browser language
      initialLanguage = this.translateService.getBrowserLang();
    }
    const language = LanguageService.getFrontendLanguage(initialLanguage);
    // eslint-disable-next-line rxjs-x/no-floating-observables
    this.translateService.setDefaultLang(DEFAULT_TRANSLATION_LANG);
    return this.setLanguage$(language);
  }

  public static getFrontendLanguage(language: string | undefined): FrontendLanguage {
    if (language && LanguageService.isSupportedLanguage(language)) {
      return language;
    }
    return DEFAULT_LANG;
  }

  private static isSupportedLanguage(language: string): language is FrontendLanguage {
    const stringSupportedLanguages = SUPPORTED_LANGS as string[];
    return stringSupportedLanguages.includes(language);
  }

  public getCurrentLanguage$(): Observable<FrontendLanguage> {
    return this.translateService.onLangChange.pipe(
      map((event: LangChangeEvent) => event.lang),
      startWith(this.translateService.currentLang),
      map((language: string) => LanguageService.getFrontendLanguage(language)),
    );
  }
}
